// SEO utilities for generating slugs and metadata
import type { Car, Agency } from '../types/database'
import { CANONICAL_DOMAIN } from './canonicalUrl'

/**
 * Generate SEO-friendly slug from car data with location and localized prefix
 */
export function generateCarSlug(car: Car, primaryLocation?: string, lang: string = 'fr'): string {
  const { make, model, year, category, id } = car
  
  // Localized prefixes
  const prefixes = {
    fr: 'location-voiture',
    en: 'rent-car',
    ar: 'تأجير-سيارة'
  }
  
  const prefix = prefixes[lang as keyof typeof prefixes] || prefixes.fr
  
  // Clean and format parts
  const cleanPart = (part: string) => part.toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06FF]+/g, '-') // Support Arabic characters
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  
  // Create enhanced slug: prefix-carName-primaryLocation-category-id
  const slugParts = [
    prefix,
    `${make}-${model}-${year}`,
    primaryLocation ? cleanPart(primaryLocation) : '',
    cleanPart(category),
    id.slice(0, 8) // Use first 8 characters of UUID for uniqueness
  ].filter(Boolean) // Remove empty parts
  
  const slug = slugParts
    .map(part => cleanPart(part))
    .join('-')
    .replace(/-+/g, '-') // Remove multiple consecutive dashes
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes
  
  return slug
}

/**
 * Generate SEO-friendly slug from car data (legacy version for backward compatibility)
 */
export function generateCarSlugLegacy(car: Car): string {
  const { make, model, year, category, id } = car
  
  // Create slug: make-model-year-category-id
  const slug = [
    make,
    model,
    year.toString(),
    category,
    id.slice(0, 8) // Use first 8 characters of UUID for uniqueness
  ]
    .map(part => part.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
    .join('-')
    .replace(/-+/g, '-') // Remove multiple consecutive dashes
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes
  
  return slug
}

/**
 * Generate SEO-friendly slug from agency data
 */
export function generateAgencySlug(agency: { id: string; name: string; city?: string }): string {
  const { name, city, id } = agency
  
  // Create slug: name-city-id
  const slug = [
    name,
    city || '',
    id.slice(0, 8) // Use first 8 characters of UUID for uniqueness
  ]
    .filter(Boolean) // Remove empty strings
    .map(part => part.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
    .join('-')
    .replace(/-+/g, '-') // Remove multiple consecutive dashes
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes
  
  return slug
}

/**
 * Extract agency ID from SEO slug
 */
export function extractAgencyIdFromSlug(slug: string): string | null {
  // The ID is the last part after the final dash
  const parts = slug.split('-')
  const idPart = parts[parts.length - 1]
  
  // Validate that it looks like a UUID fragment (8 characters, alphanumeric)
  if (idPart && idPart.length === 8 && /^[a-z0-9]+$/.test(idPart)) {
    return idPart
  }
  
  return null
}

/**
 * Extract car ID from SEO slug (supports both legacy and enhanced formats)
 */
export function extractCarIdFromSlug(slug: string): string | null {
  // The ID is the last part after the final dash
  const parts = slug.split('-')
  const idPart = parts[parts.length - 1]
  
  // Check if the last part is a UUID fragment (8 characters, alphanumeric)
  if (idPart && idPart.length === 8 && /^[a-z0-9]+$/.test(idPart)) {
    // Look for the full UUID pattern in the slug first
    // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const uuidPattern = /([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i
    const uuidMatch = slug.match(uuidPattern)
    
    if (uuidMatch) {
      return uuidMatch[1]
    }
    
    // For 8-character fragments, we need to look up the full UUID in the database
    // This will be handled by the calling code that has access to Supabase
    // Return the 8-character fragment for now
    return idPart
  }
  
  return null
}

/**
 * Extract components from enhanced SEO slug
 */
export function extractCarSlugComponents(slug: string, lang: string = 'fr'): {
  prefix: string | null;
  carName: string | null;
  location: string | null;
  category: string | null;
  id: string | null;
} {
  const prefixes = {
    fr: 'location-voiture',
    en: 'rent-car',
    ar: 'تأجير-سيارة'
  }
  
  const expectedPrefix = prefixes[lang as keyof typeof prefixes] || prefixes.fr
  const parts = slug.split('-')
  
  // Check if it's an enhanced slug (starts with expected prefix)
  if (parts.length >= 4 && slug.startsWith(expectedPrefix)) {
    // Enhanced format: prefix-carName-location-category-id
    const prefixLength = expectedPrefix.split('-').length
    const id = parts[parts.length - 1]
    const category = parts[parts.length - 2]
    const location = parts.length > prefixLength + 2 ? parts[parts.length - 3] : null
    
    // Car name is everything between prefix and location/category/id
    const carNameStart = prefixLength
    const carNameEnd = parts.length - (location ? 3 : 2)
    const carName = parts.slice(carNameStart, carNameEnd).join('-')
    
    return {
      prefix: expectedPrefix,
      carName: carName || null,
      location,
      category,
      id: id && id.length === 8 && /^[a-z0-9]+$/.test(id) ? id : null
    }
  }
  
  // Legacy format or unrecognized format
  return {
    prefix: null,
    carName: null,
    location: null,
    category: null,
    id: extractCarIdFromSlug(slug)
  }
}

/**
 * Generate car page title for SEO
 */
export function generateCarTitle(car: Car, t: (key: string) => string, lang: string = 'fr'): string {
  const { make, model, year, category } = car
  
  const titles = {
    fr: `${make} ${model} ${year} - Location ${category} | KiraCar Maroc`,
    en: `${make} ${model} ${year} - ${category} Rental | KiraCar Morocco`,
    ar: `${make} ${model} ${year} - تأجير ${category} | كيراكار المغرب`
  }
  
  return titles[lang as keyof typeof titles] || titles.fr
}

/**
 * Generate car page description for SEO
 */
export function generateCarDescription(car: Car, agency: Agency | null, t: (key: string) => string, lang: string = 'fr'): string {
  const { make, model, year, price_per_day, seats, transmission, fuel_type } = car
  const agencyName = agency?.name || ''
  
  const descriptions = {
    fr: `Louez une ${make} ${model} ${year} à partir de ${price_per_day} DH/jour. ${seats} places, ${transmission === 'automatic' ? 'automatique' : 'manuelle'}, ${fuel_type}. ${agencyName ? `Disponible chez ${agencyName}.` : ''} Réservation en ligne facile au Maroc.`,
    en: `Rent a ${make} ${model} ${year} from ${price_per_day} DH/day. ${seats} seats, ${transmission}, ${fuel_type}. ${agencyName ? `Available at ${agencyName}.` : ''} Easy online booking in Morocco.`,
    ar: `استأجر ${make} ${model} ${year} من ${price_per_day} درهم/يوم. ${seats} مقاعد، ${transmission === 'automatic' ? 'أوتوماتيك' : 'يدوي'}، ${fuel_type}. ${agencyName ? `متوفر في ${agencyName}.` : ''} حجز سهل عبر الإنترنت في المغرب.`
  }
  
  return descriptions[lang as keyof typeof descriptions] || descriptions.fr
}

/**
 * Generate structured data (JSON-LD) for car page
 */
export function generateCarStructuredData(car: Car, agency: Agency | null, currentUrl: string): object {
  const { make, model, year, price_per_day, images, features, seats, transmission, fuel_type, description } = car
  
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${make} ${model} ${year}`,
    "description": description || `${make} ${model} ${year} car rental`,
    "category": "Car Rental",
    "brand": {
      "@type": "Brand",
      "name": make
    },
    "model": model,
    "vehicleModelDate": year,
    "image": images && images.length > 0 ? images : ["/images/cars/default-car.jpg"],
    "offers": {
      "@type": "Offer",
      "price": price_per_day,
      "priceCurrency": "MAD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      "seller": agency ? {
        "@type": "Organization",
        "name": agency.name,
        "telephone": agency.phone,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": agency.city,
          "addressCountry": "MA"
        }
      } : undefined
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Seats",
        "value": seats
      },
      {
        "@type": "PropertyValue", 
        "name": "Transmission",
        "value": transmission
      },
      {
        "@type": "PropertyValue",
        "name": "Fuel Type", 
        "value": fuel_type
      },
      ...(features || []).map(feature => ({
        "@type": "PropertyValue",
        "name": "Feature",
        "value": feature
      }))
    ],
    "url": currentUrl,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "reviewCount": "12",
      "bestRating": "5",
      "worstRating": "1"
    }
  }
}

/**
 * Generate agency page title for SEO
 * Format:
 * en: [Agency Name] Car Rental Agency in [City] – Prices & Reviews – KiraCar
 * fr: [Nom de l'agence] Agence de Location de Voiture à [Ville] – Tarifs & Avis – KiraCar
 * ar: [اسم الوكالة] وكالة كراء السيارات في [المدينة] ـ اسعار وتقيمات ـ KiraCar
 */
export function generateAgencyTitle(agency: { name: string; city?: string }, t: (key: string) => string, lang: string = 'fr'): string {
  const { name, city } = agency
  
  // Capitalize first letter of city name
  const formattedCity = city 
    ? city.charAt(0).toUpperCase() + city.slice(1).toLowerCase()
    : undefined
  
  const titles = {
    en: formattedCity 
      ? `${name} Car Rental Agency in ${formattedCity} – Prices & Reviews – KiraCar`
      : `${name} Car Rental Agency – Prices & Reviews – KiraCar`,
    fr: formattedCity 
      ? `${name} Agence de Location de Voiture à ${formattedCity} – Tarifs & Avis – KiraCar`
      : `${name} Agence de Location de Voiture – Tarifs & Avis – KiraCar`,
    ar: formattedCity 
      ? `${name} وكالة كراء السيارات في ${formattedCity} ـ اسعار وتقيمات ـ KiraCar`
      : `${name} وكالة كراء السيارات ـ اسعار وتقيمات ـ KiraCar`
  }
  
  return titles[lang as keyof typeof titles] || titles.fr
}

/**
 * Generate agency page description for SEO
 */
export function generateAgencyDescription(agency: { name: string; city?: string; description?: string }, t: (key: string) => string, lang: string = 'fr'): string {
  const { name, city, description } = agency
  
  const descriptions = {
    fr: `Découvrez ${name}${city ? ` à ${city}` : ''} - Agence de location de voitures au Maroc. ${description || 'Large choix de véhicules, prix compétitifs et service de qualité.'} Réservez votre voiture en ligne.`,
    en: `Discover ${name}${city ? ` in ${city}` : ''} - Car rental agency in Morocco. ${description || 'Wide selection of vehicles, competitive prices and quality service.'} Book your car online.`,
    ar: `اكتشف ${name}${city ? ` في ${city}` : ''} - وكالة تأجير السيارات في المغرب. ${description || 'مجموعة واسعة من المركبات وأسعار تنافسية وخدمة عالية الجودة.'} احجز سيارتك عبر الإنترنت.`
  }
  
  return descriptions[lang as keyof typeof descriptions] || descriptions.fr
}

/**
 * Generate agency page keywords for SEO
 */
export function generateAgencyKeywords(agency: { name: string; city?: string }, lang: string = 'fr'): string {
  const { name, city } = agency
  
  const keywordSets = {
    fr: [
      `${name.toLowerCase()}`,
      city ? `location voiture ${city.toLowerCase()}` : '',
      'agence location voiture maroc',
      'rent car morocco',
      city ? `${city.toLowerCase()} car rental` : '',
      'kiracar',
      'location auto',
      'agence automobile'
    ],
    en: [
      `${name.toLowerCase()}`,
      city ? `car rental ${city.toLowerCase()}` : '',
      'car rental agency morocco',
      'rent car morocco',
      city ? `${city.toLowerCase()} car hire` : '',
      'kiracar',
      'vehicle rental',
      'auto rental'
    ],
    ar: [
      `${name}`,
      city ? `تأجير سيارات ${city}` : '',
      'وكالة تأجير سيارات المغرب',
      'كيراكار',
      'تأجير السيارات',
      'إيجار السيارات'
    ]
  }
  
  return (keywordSets[lang as keyof typeof keywordSets] || keywordSets.fr)
    .filter(Boolean)
    .join(', ')
}

/**
 * Generate car page keywords for SEO
 */
export function generateCarKeywords(car: Car, agency: Agency | null, lang: string = 'fr'): string {
  const { make, model, year, category, transmission, fuel_type } = car
  const agencyName = agency?.name || ''
  
  const keywordSets = {
    fr: [
      `location ${make.toLowerCase()}`,
      `${make.toLowerCase()} ${model.toLowerCase()}`,
      `location voiture ${category.toLowerCase()}`,
      `location auto maroc`,
      `${make.toLowerCase()} ${year}`,
      `voiture ${transmission === 'automatic' ? 'automatique' : 'manuelle'}`,
      `location ${fuel_type}`,
      agencyName ? `${agencyName.toLowerCase()} location` : '',
      'kiracar',
      'location voiture casablanca',
      'location voiture rabat',
      'rent car morocco'
    ],
    en: [
      `${make.toLowerCase()} rental`,
      `${make.toLowerCase()} ${model.toLowerCase()}`,
      `${category.toLowerCase()} car rental`,
      `car rental morocco`,
      `${make.toLowerCase()} ${year}`,
      `${transmission} car`,
      `${fuel_type} vehicle`,
      agencyName ? `${agencyName.toLowerCase()} rental` : '',
      'kiracar',
      'casablanca car rental',
      'rabat car rental',
      'morocco car hire'
    ],
    ar: [
      `تأجير ${make}`,
      `${make} ${model}`,
      `تأجير سيارات ${category}`,
      `تأجير سيارات المغرب`,
      `${make} ${year}`,
      `سيارة ${transmission === 'automatic' ? 'أوتوماتيك' : 'يدوي'}`,
      agencyName ? `${agencyName} تأجير` : '',
      'كيراكار',
      'تأجير سيارات الدار البيضاء',
      'تأجير سيارات الرباط'
    ]
  }
  
  return (keywordSets[lang as keyof typeof keywordSets] || keywordSets.fr)
    .filter(Boolean)
    .join(', ')
}

/**
 * Generate Open Graph metadata for car page
 */
export function generateCarOpenGraph(car: Car, title: string, description: string, currentUrl: string): Record<string, string> {
  const { images } = car
  const mainImage = images && images.length > 0 ? images[0] : '/images/cars/default-car.jpg'
  
  return {
    'og:title': title,
    'og:description': description,
    'og:type': 'product',
    'og:url': currentUrl,
    'og:image': mainImage,
    'og:image:alt': `${car.make} ${car.model} ${car.year}`,
    'og:site_name': 'KiraCar',
    'og:locale': 'fr_MA',
    'product:price:amount': car.price_per_day.toString(),
    'product:price:currency': 'MAD'
  }
}

/**
 * Generate Twitter Card metadata for car page
 */
export function generateCarTwitterCard(car: Car, title: string, description: string): Record<string, string> {
  const { images } = car
  const mainImage = images && images.length > 0 ? images[0] : '/images/cars/default-car.jpg'

  return {
    'twitter:card': 'summary_large_image',
    'twitter:title': title,
    'twitter:description': description,
    'twitter:image': mainImage,
    'twitter:image:alt': `${car.make} ${car.model} ${car.year}`
  }
}

/**
 * Generate BreadcrumbList structured data
 */
export function generateBreadcrumbSchema(breadcrumbs: Array<{ name: string; url: string }>): object {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  }
}

/**
 * Generate Organization structured data for KiraCar
 */
export function generateOrganizationSchema(lang: string = 'fr'): object {
  const names = {
    fr: 'KiraCar - Location de Voitures au Maroc',
    en: 'KiraCar - Car Rental in Morocco',
    ar: 'كيراكار - تأجير السيارات في المغرب'
  }

  const descriptions = {
    fr: 'Plateforme de location de voitures au Maroc. Réservez votre véhicule en ligne avec les meilleures agences de location.',
    en: 'Car rental platform in Morocco. Book your vehicle online with the best rental agencies.',
    ar: 'منصة تأجير السيارات في المغرب. احجز سيارتك عبر الإنترنت مع أفضل وكالات التأجير.'
  }

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "KiraCar",
    "alternateName": names[lang as keyof typeof names] || names.fr,
    "url": CANONICAL_DOMAIN,
    "logo": `${CANONICAL_DOMAIN}/logo.png`,
    "description": descriptions[lang as keyof typeof descriptions] || descriptions.fr,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "MA",
      "addressRegion": "Morocco"
    },
    "sameAs": [
      "https://www.facebook.com/kiracar",
      "https://www.instagram.com/kiracar",
      "https://twitter.com/kiracar"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "availableLanguage": ["French", "English", "Arabic"]
    }
  }
}

/**
 * Generate LocalBusiness structured data for agencies
 */
export function generateLocalBusinessSchema(agency: Agency & { latitude?: number; longitude?: number; logo?: string; images?: string[]; business_hours?: any }, lang: string = 'fr'): object {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": agency.name,
    "description": agency.description || `Car rental agency in ${agency.city || 'Morocco'}`,
    "telephone": agency.phone,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": agency.city,
      "addressCountry": "MA"
    },
    "priceRange": "$$",
    "url": `${CANONICAL_DOMAIN}/${lang}/agencies/${generateAgencySlug(agency)}`
  }

  // Add optional geo coordinates if available
  if (agency.latitude && agency.longitude) {
    schema.geo = {
      "@type": "GeoCoordinates",
      "latitude": agency.latitude,
      "longitude": agency.longitude
    }
  }

  // Add image if available
  if (agency.logo || agency.images?.[0]) {
    schema.image = agency.logo || agency.images?.[0]
  }

  // Add business hours if available
  if (agency.business_hours) {
    schema.openingHoursSpecification = {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "08:00",
      "closes": "20:00"
    }
  }

  return schema
}

/**
 * Generate FAQPage structured data
 */
export function generateFAQSchema(faqs: Array<{ question: string; answer: string }>): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  }
}

/**
 * Generate ItemList structured data for car listings
 */
export function generateItemListSchema(cars: Car[], currentUrl: string): object {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": cars.map((car, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": `${car.make} ${car.model} ${car.year}`,
        "image": car.images?.[0] || "/images/cars/default-car.jpg",
        "offers": {
          "@type": "Offer",
          "price": car.price_per_day,
          "priceCurrency": "MAD"
        }
      }
    })),
    "url": currentUrl
  }
}

/**
 * Generate WebSite structured data with search action
 */
export function generateWebSiteSchema(lang: string = 'fr'): object {
  const searchTemplates = {
    fr: `${CANONICAL_DOMAIN}/fr/cars?search={search_term_string}`,
    en: `${CANONICAL_DOMAIN}/en/cars?search={search_term_string}`,
    ar: `${CANONICAL_DOMAIN}/ar/cars?search={search_term_string}`
  }

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "KiraCar",
    "url": CANONICAL_DOMAIN,
    "potentialAction": {
      "@type": "SearchAction",
      "target": searchTemplates[lang as keyof typeof searchTemplates] || searchTemplates.fr,
      "query-input": "required name=search_term_string"
    }
  }
}
